import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import {
  useGetEdgeShiftHistorySessionsQuery,
  useGetTillsQuery,
} from "../../store/api";
import {
  formatCurrency,
  formatSignedCurrency,
  getCashingUpSessionStatusLabel,
  getCashingUpStatusTone,
  normalizeMoney,
} from "../../utils/cashingUp";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const formatShortDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const extractRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.reports)) return data.reports;
  if (Array.isArray(data?.sessions)) return data.sessions;
  return [];
};

const parseJsonObject = (value) => {
  if (!value || typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const parseMoney = (value) => normalizeMoney(value);

const HISTORY_STATUS_OPTIONS = [
  "OPEN",
  "CASH_UP_IN_PROGRESS",
  "COUNT_SUBMITTED",
  "CLOSED",
  "REOPENED",
];

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
    <path
      d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const clampToToday = (value, today) => {
  if (!value) return today;
  return value > today ? today : value;
};

const ShiftHistory = () => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const initialDateFrom =
    clampToToday(
      searchParams.get("fromDate") ||
        searchParams.get("dateFrom") ||
        searchParams.get("businessDate") ||
        today,
      today,
    );
  const initialDateTo =
    clampToToday(
      searchParams.get("toDate") ||
        searchParams.get("dateTo") ||
        searchParams.get("businessDate") ||
        today,
      today,
    );

  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(initialDateTo);
  const [tillIdFilter, setTillIdFilter] = useState(searchParams.get("tillId") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("statuses") || "all");

  const { data: tillsData } = useGetTillsQuery(
    { storeId: selectedStoreId, activeOnly: false },
    { skip: !selectedStoreId },
  );

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );

  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );

  const queryParams = useMemo(
    () => ({
      fromDate: dateFrom || undefined,
      toDate: dateTo || undefined,
      tillId: tillIdFilter !== "all" ? tillIdFilter : undefined,
      statuses: statusFilter !== "all" ? statusFilter : undefined,
      limit: 200,
    }),
    [dateFrom, dateTo, statusFilter, tillIdFilter],
  );

  const {
    data: shiftHistoryData,
    isLoading: shiftHistoryLoading,
    isError: shiftHistoryIsError,
    error: shiftHistoryError,
    refetch: refetchShiftHistory,
  } = useGetEdgeShiftHistorySessionsQuery(queryParams, {
    skip: !selectedStoreId || !dateFrom || !dateTo,
  });

  const shiftHistoryRows = useMemo(
    () => extractRows(shiftHistoryData),
    [shiftHistoryData],
  );

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (dateFrom) nextParams.set("fromDate", dateFrom);
    if (dateTo) nextParams.set("toDate", dateTo);
    if (tillIdFilter !== "all") nextParams.set("tillId", tillIdFilter);
    if (statusFilter !== "all") nextParams.set("statuses", statusFilter);
    setSearchParams(nextParams, { replace: true });
  }, [dateFrom, dateTo, setSearchParams, statusFilter, tillIdFilter]);

  const activeFilterQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("fromDate", dateFrom);
    if (dateTo) params.set("toDate", dateTo);
    if (tillIdFilter !== "all") params.set("tillId", tillIdFilter);
    if (statusFilter !== "all") params.set("statuses", statusFilter);
    return params.toString();
  }, [dateFrom, dateTo, statusFilter, tillIdFilter]);

  const rows = useMemo(
    () =>
      shiftHistoryRows.map((row) => {
        const rowSessionId = row.cashSessionId || row.sessionId || row.id || "";
        const rowStatus = String(row.status || "").toUpperCase();
        const rowTillName =
          row.tillName || tillById.get(row.tillId)?.tillName || row.tillId || "-";
        const closingCount = parseJsonObject(row.closingCountJson);
        const rowStartingCash = parseMoney(row.openingCash);
        const rowCashMovements = parseMoney(
          closingCount.cashTakenFromDrawer ??
            row.cashTakenFromDrawer ??
            row.cashMovementTotal ??
            row.movementTotal ??
            0,
        );
        const rowCountedCash = parseMoney(row.actualCash || row.closingCash);
        const rowVariance = parseMoney(row.cashVariance || row.varianceAmount);

        return {
          id: rowSessionId,
          className:
            rowStatus === "CLOSED"
              ? "bg-indigo-50/60 dark:bg-indigo-950/20"
              : "",
          tillName: rowTillName,
          businessDate: row.businessDate || "-",
          openedAt: formatShortDateTime(row.openedAt),
          closedAt: formatShortDateTime(row.closedAt),
          status: (
            <Badge tone={getCashingUpStatusTone(rowStatus)}>
              {getCashingUpSessionStatusLabel(row.status)}
            </Badge>
          ),
          startingCash: formatCurrency(rowStartingCash),
          cashMovements: formatCurrency(rowCashMovements),
          countedCash: formatCurrency(rowCountedCash),
          variance: (
            <span
              className={`font-semibold ${
                rowVariance >= 0
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-rose-700 dark:text-rose-300"
              }`}
            >
              {formatSignedCurrency(rowVariance)}
            </span>
          ),
          actions: (
            <button
              type="button"
              title="View summary"
              aria-label={`View summary for ${
                row.localReferenceNo || row.localReference || rowSessionId
              }`}
              onClick={() =>
                navigate(
                  `/pos/cash-movements/history/${rowSessionId}${
                    activeFilterQueryString ? `?${activeFilterQueryString}` : ""
                  }`,
                )
              }
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-200"
            >
              <EyeIcon />
            </button>
          ),
        };
      }),
    [activeFilterQueryString, navigate, shiftHistoryRows, tillById],
  );

  if (!selectedStoreId) {
    return (
      <div className="space-y-6 px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
        <PageHeader title="" subtitle="Review shift history for the selected date range." />
        <Card className="border-slate-200 bg-white/95 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
          <Alert tone="warning">No store is selected. Choose a store first.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 pb-6 pt-3 sm:px-6 lg:px-8 lg:pt-4">
      <PageHeader
        title=""
        subtitle="Review shift history for the selected date range."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/pos/cash-movements")}
            >
              Back to Cash Movements
            </Button>
          </div>
        }
      />

      <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
        <div className="grid gap-4 lg:grid-cols-[160px_160px_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <Input
            label="Date From"
            type="date"
            max={today}
            value={dateFrom}
            onChange={(e) => setDateFrom(clampToToday(e.target.value, today))}
          />
          <Input
            label="Date To"
            type="date"
            max={today}
            value={dateTo}
            onChange={(e) => setDateTo(clampToToday(e.target.value, today))}
          />
          <Select
            label="Till"
            value={tillIdFilter}
            onChange={(e) => setTillIdFilter(e.target.value)}
          >
            <option value="all">All Tills</option>
            {tills.map((till) => (
              <option key={till.tillId} value={till.tillId}>
                {till.tillName || till.tillCode || till.tillId}
              </option>
            ))}
          </Select>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {HISTORY_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {getCashingUpSessionStatusLabel(status)}
              </option>
            ))}
          </Select>
          <div className="flex items-end">
            <Button
              size="sm"
              variant="secondary"
              className="h-11 whitespace-nowrap px-4"
              onClick={() => {
                setDateFrom(today);
                setDateTo(today);
                setTillIdFilter("all");
                setStatusFilter("all");
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {shiftHistoryIsError && (
        <Alert tone="error">
          {getErrorMessage(shiftHistoryError, "Failed to load shift history.")}
        </Alert>
      )}

      {shiftHistoryLoading && <Alert tone="info">Loading shift history...</Alert>}

      {!shiftHistoryLoading && !shiftHistoryRows.length && (
        <Alert tone="warning">
          No sessions were found for the selected date range and filters.
        </Alert>
      )}

      <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Shift Sessions
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Use the eye icon to open the Shift Summary page.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 dark:text-slate-400">Rows</p>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {shiftHistoryRows.length}
            </p>
          </div>
        </div>

        {!shiftHistoryLoading && shiftHistoryRows.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
            <Table
              columns={[
                { key: "tillName", label: "Till Name" },
                { key: "businessDate", label: "Business Date" },
                { key: "openedAt", label: "Opened At" },
                { key: "closedAt", label: "Closed At" },
                { key: "status", label: "Status" },
                { key: "startingCash", label: "Starting Cash" },
                { key: "cashMovements", label: "Cash Movements" },
                { key: "countedCash", label: "Counted Cash" },
                { key: "variance", label: "Variance" },
                { key: "actions", label: "", headerClassName: "w-16" },
              ]}
              rows={rows}
              emptyText="No shift rows found."
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ShiftHistory;
